import { useId, useState } from "react";
import { Pencil, Save, X, LoaderCircle } from "lucide-react";
import ProfileIconButton from "./ProfileIconButton";
import { FormSelect } from "../../../components/ui/select";
import { buildProfileUpdate, profileDraft, yesterdayDate } from "../services/profileFields";

export default function ProfileSectionEditor({ fields, record, canEdit, creating = false, onSave, editLabel, children, renderShell, compact = false }) {
  const id = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  function startEditing() {
    setDraft(profileDraft(fields, record));
    setErrors({});
    setMessage(null);
    setEditing(true);
  }

  async function save(event) {
    event.preventDefault();
    if (saving || !canEdit) return;
    const result = buildProfileUpdate(fields, record, draft, creating);
    setErrors(result.errors);
    setMessage(null);
    if (Object.keys(result.errors).length) return;
    setSaving(true);
    try {
      if (Object.keys(result.payload).length) await onSave(result.payload);
      setEditing(false);
      setMessage({ type: "success", text: "Changes saved." });
    } catch (error) {
      const fieldErrors = error.data?.fieldErrors || error.data?.errors || error.data;
      if (fieldErrors && typeof fieldErrors === "object" && !Array.isArray(fieldErrors)) {
        setErrors(Object.fromEntries(fields
          .filter(({ key }) => typeof fieldErrors[key] === "string")
          .map(({ key }) => [key, fieldErrors[key]])));
      }
      setMessage({
        type: "error",
        text: error.status === 403
          ? "Your account does not have permission to save these details. Please contact HR."
          : error.message || "Unable to save changes. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  }

  const editAction = canEdit && !editing ? (
    <ProfileIconButton icon={Pencil} label={editLabel} onClick={startEditing} />
  ) : null;
  const content = (
    <div className="space-y-4">
      {editing && canEdit ? (
        <form onSubmit={save} noValidate className="space-y-4">
          <fieldset disabled={saving} className={"grid grid-cols-1 gap-4 border-0 p-0 m-0 min-w-0 " + (compact ? "" : "sm:grid-cols-2")}>
            {fields.map((field) => {
              const fieldId = id + "-" + field.key;
              const value = draft[field.key] ?? "";
              const required = field.required || (creating && field.requiredOnCreate);
              const props = {
                id: fieldId,
                name: field.key,
                value,
                required,
                "aria-invalid": Boolean(errors[field.key]),
                "aria-describedby": errors[field.key] ? fieldId + "-error" : undefined,
                className: "w-full rounded-xl border border-(--color-text-border) px-3 py-2.5 text-sm text-foreground bg-white focus:outline-none focus:ring-2 focus:ring-accent/20",
                onChange: (event) => {
                  setDraft((current) => ({ ...current, [field.key]: event.target.value }));
                  setErrors((current) => ({ ...current, [field.key]: "" }));
                },
              };
              return (
                <div key={field.key} className={field.type === "textarea" ? "sm:col-span-2" : ""}>
                  <label htmlFor={fieldId} className={compact ? "sr-only" : "block text-sm font-medium text-foreground mb-1.5"}>
                    {field.label}{required ? " *" : ""}
                  </label>
                  {field.options ? (
                    <FormSelect {...props}>
                      <option value="" disabled={field.cannotClear}>Select {field.label.toLowerCase()}</option>
                      {value && !field.options.includes(value) && <option value={value}>{value}</option>}
                      {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
                    </FormSelect>
                  ) : field.type === "textarea" ? (
                    <textarea {...props} rows={3} maxLength={field.maxLength} />
                  ) : (
                    <input {...props} type={field.type || "text"} maxLength={field.maxLength}
                      inputMode={field.inputMode} min={field.min}
                      max={field.type === "date" ? yesterdayDate() : field.max}
                      step={field.type === "number" ? 1 : undefined} />
                  )}
                  {errors[field.key] && <p id={fieldId + "-error"} role="alert" className="text-sm text-error mt-1">{errors[field.key]}</p>}
                </div>
              );
            })}
          </fieldset>
          <div className="flex flex-wrap gap-2">
            <ProfileIconButton type="submit" icon={saving ? LoaderCircle : Save} spinning={saving}
              label={saving ? "Saving changes" : "Save changes"} disabled={saving} />
            <ProfileIconButton icon={X} label="Cancel changes" disabled={saving}
              onClick={() => { setEditing(false); setMessage(null); }} />
          </div>
        </form>
      ) : (
        <>
          {children}
          {!renderShell && editAction}
        </>
      )}
      {message && (
        <p role={message.type === "error" ? "alert" : "status"} className={"text-sm m-0 " + (message.type === "error" ? "text-error" : "text-success")}>
          {message.text}
        </p>
      )}
    </div>
  );
  return renderShell ? renderShell({ content, editAction, editing }) : content;
}
